import React from 'react'
import styled from 'styled-components'
import { Draggable } from 'react-beautiful-dnd'
import i18n from 'live/services/i18n'
import { ColorStyle } from '../api'
import { ChannelRowData, getDotColor, getTextTintColor } from './channelRowHelpers'
import { RowColorMark, RowInfo, RowMeta, RowMetaLine, RowTitle } from './rowStyles'
import { DynamicChannelBadge } from './DynamicChannelBadge'

type Props = {
    row: ChannelRowData
    index: number
    colorStyle: ColorStyle
    colorTextEnabled: boolean
    onAdd: (mnemonic: string) => void
}

// no drag handle icon here - the whole row is the drag surface, since there's nothing to visually
// call out as "manual sort" in this panel (an available channel has no display order of its own)
export const AvailableChannelRow = React.memo(function AvailableChannelRow({
    row,
    index,
    colorStyle,
    colorTextEnabled,
    onAdd
}: Props): JSX.Element {
    const { metadata } = row
    const info = metadata.category ? [metadata.mnemonic, `Category: ${metadata.category}`] : [metadata.mnemonic]
    const tintColor = colorTextEnabled ? getTextTintColor(row) : null

    return (
        <Draggable draggableId={metadata.mnemonic} index={index}>
            {provided => (
                <Row ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                    <RowColorMark color={getDotColor(row)} shape={colorStyle} />
                    <RowInfo>
                        <RowTitle style={tintColor ? { color: tintColor } : undefined}>{metadata.title}</RowTitle>
                        <RowMetaLine>
                            {metadata.selected === true && <DynamicChannelBadge />}
                            <RowMeta>{info.join(' · ')}</RowMeta>
                        </RowMetaLine>
                    </RowInfo>
                    <AddButton onClick={() => onAdd(metadata.mnemonic)}>
                        <i className="fa fa-plus" />
                        {i18n('Add')}
                    </AddButton>
                </Row>
            )}
        </Draggable>
    )
})

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    height: 48px;
    padding: 0 16px 0 12px;
    border-bottom: 0.5px solid var(--gray-300);
    cursor: grab;
`

const AddButton = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 18px;
    color: var(--blue-500);
    background: none;
    border: none;
    padding: 4px 6px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        background-color: var(--gray-200);
    }
`
